import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Plus, Loader2, ShieldCheck, Sparkles, Clock,
  CheckCircle2, XCircle, AlertCircle, Eye, Calendar, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import useAuthStore from "../store/authStore";
import { listContracts, STATUS, STATUS_META, transition } from "../services/b2bContracts";
import ContractDraftDialog from "../components/contracts/ContractDraftDialog";
import ContractReviewDialog from "../components/contracts/ContractReviewDialog";
import ContractDetailDialog from "../components/contracts/ContractDetailDialog";

/**
 * B2BContracts — Iteration 65 Phase 1 (Contract Pricing & Approval)
 *
 * Role-aware single page:
 *  • admin / founder / ceo → sees pending approvals, all contracts,
 *    and can approve/reject/activate.
 *  • city_manager          → sees own drafts, can create + submit;
 *    cannot approve.
 *  • is_b2b customer       → sees own active contract (read-only) on
 *    the "/dashboard/b2b/my-contract" route (separate page); this page
 *    refuses to render for plain customers.
 *
 * Phase 2 (next iteration) will add analytics, simulator UI, and
 * customer portal screens — the API surface already supports them.
 */
export default function B2BContracts() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const isAdmin = ["admin", "founder", "ceo"].includes(user?.role);
  const isManager = user?.role === "city_manager";
  const canAccess = isAdmin || isManager;

  const [tab, setTab] = useState(isAdmin ? "pending" : "drafts");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    if (!canAccess) navigate("/dashboard", { replace: true });
  }, [canAccess, navigate]);

  const fetchTab = async () => {
    setLoading(true);
    try {
      let params = { limit: 100 };
      if (tab === "pending") params.status = STATUS.SUBMITTED;
      else if (tab === "under_review") params.status = STATUS.UNDER_REVIEW;
      else if (tab === "approved") params.status = STATUS.APPROVED;
      else if (tab === "active") params.status = STATUS.ACTIVE;
      else if (tab === "rejected") params.status = STATUS.REJECTED;
      else if (tab === "drafts") {
        params.status = STATUS.DRAFT;
        if (isManager) params.mine = true;
      }
      const { contracts } = await listContracts(params);
      setItems(contracts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (canAccess) fetchTab(); /* eslint-disable-line */ }, [tab, canAccess]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((c) =>
      (c.company_name || "").toLowerCase().includes(q)
      || (c.origin_city || "").toLowerCase().includes(q)
      || (c.gstin || "").toLowerCase().includes(q),
    );
  }, [items, query]);

  const onQuickActivate = async (id) => {
    try {
      await transition(id, "activate");
      toast.success("Contract activated");
      fetchTab();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    }
  };

  if (!canAccess) return null;

  const tabs = isAdmin
    ? [
        { key: "pending",       label: "Pending approval", icon: Clock },
        { key: "under_review",  label: "Under review",     icon: Eye },
        { key: "approved",      label: "Approved",         icon: CheckCircle2 },
        { key: "active",        label: "Active",           icon: ShieldCheck },
        { key: "rejected",      label: "Rejected",         icon: XCircle },
        { key: "drafts",        label: "All drafts",       icon: FileText },
      ]
    : [
        { key: "drafts",   label: "My drafts",        icon: FileText },
        { key: "pending",  label: "Awaiting approval", icon: Clock },
        { key: "active",   label: "Active in my city", icon: ShieldCheck },
        { key: "rejected", label: "Rejected",          icon: XCircle },
      ];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="b2b-contracts-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" /> Phase 1 · Contract pricing
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
            B2B Contracts
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin ? "Approve, activate, and govern company pricing." : "Create proposals for your city; admin approves."}
          </p>
        </div>
        <Button
          onClick={() => { setDraftId(null); setDraftOpen(true); }}
          className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-11 px-5"
          data-testid="b2b-contracts-new-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New proposal
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-xl flex-wrap h-auto p-1 mb-4 w-full justify-start">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="data-[state=active]:bg-[#FF6B00] data-[state=active]:text-white rounded-lg px-3 py-1.5 text-xs sm:text-sm"
                data-testid={`b2b-tab-${t.key}`}
              >
                <Icon className="h-3.5 w-3.5 mr-1.5" />
                {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Search */}
        <div className="mb-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by company, city, or GSTIN…"
            className="rounded-xl border-slate-200 h-10 max-w-xs"
            data-testid="b2b-contracts-search"
          />
        </div>

        <TabsContent value={tab} className="m-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState tab={tab} isAdmin={isAdmin} onNew={() => { setDraftId(null); setDraftOpen(true); }} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((c) => (
                <ContractCard
                  key={c.id}
                  c={c}
                  isAdmin={isAdmin}
                  onView={() => setDetailId(c.id)}
                  onEdit={() => { setDraftId(c.id); setDraftOpen(true); }}
                  onReview={() => setReviewId(c.id)}
                  onActivate={() => onQuickActivate(c.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ContractDraftDialog
        open={draftOpen}
        contractId={draftId}
        onClose={(saved) => { setDraftOpen(false); if (saved) fetchTab(); }}
      />
      <ContractReviewDialog
        contractId={reviewId}
        onClose={(changed) => { setReviewId(null); if (changed) fetchTab(); }}
      />
      <ContractDetailDialog
        contractId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}


function EmptyState({ tab, isAdmin, onNew }) {
  const copy = {
    pending: ["Nothing waiting for approval.", "When a hub manager submits a proposal it'll show up here."],
    under_review: ["No contracts under review.", "Start a review from the Pending tab."],
    approved: ["No approved-but-inactive contracts.", "Approve a submission, then activate it from here."],
    active: ["No active contracts yet.", isAdmin ? "Activate an approved contract to start applying its rates to orders." : "Your city has no active B2B contracts yet."],
    rejected: ["No rejected contracts.", "Rejected proposals appear here so the team can clone & re-submit."],
    drafts: ["No drafts yet.", "Create a proposal to start onboarding a B2B customer."],
  }[tab] || ["Nothing here yet.", ""];
  return (
    <div className="text-center py-16" data-testid="b2b-contracts-empty">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 mb-4">
        <FileText className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-base font-semibold text-slate-700">{copy[0]}</h3>
      <p className="text-sm text-slate-500 mt-1">{copy[1]}</p>
      {tab === "drafts" && (
        <Button
          onClick={onNew}
          className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl mt-4"
          data-testid="b2b-contracts-empty-cta"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New proposal
        </Button>
      )}
    </div>
  );
}


function ContractCard({ c, isAdmin, onView, onEdit, onReview, onActivate }) {
  const meta = STATUS_META[c.status] || { label: c.status, color: "bg-slate-100 text-slate-700" };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-4 hover:border-[#FF6B00]/40 transition"
      data-testid={`b2b-contract-card-${c.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900 truncate flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            {c.company_name || "Untitled company"}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">v{c.version} · {c.gstin || "no GSTIN"}</p>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${meta.color}`}>
          {meta.label}
        </span>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-2">
        <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{fmt(c.start_date)} → {fmt(c.end_date)}</span>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">
        {c.rate_cards?.length || 0} rate card{c.rate_cards?.length === 1 ? "" : "s"} · {c.user_ids?.length || 0} user{c.user_ids?.length === 1 ? "" : "s"} · {c.origin_city || "any city"}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button
          size="sm"
          variant="outline"
          onClick={onView}
          className="rounded-lg border-slate-200 text-xs h-8"
          data-testid={`b2b-card-view-${c.id}`}
        >
          <Eye className="h-3.5 w-3.5 mr-1" /> View
        </Button>
        {c.status === STATUS.DRAFT && (
          <Button
            size="sm"
            onClick={onEdit}
            className="rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs h-8"
            data-testid={`b2b-card-edit-${c.id}`}
          >
            Edit
          </Button>
        )}
        {isAdmin && (c.status === STATUS.SUBMITTED || c.status === STATUS.UNDER_REVIEW) && (
          <Button
            size="sm"
            onClick={onReview}
            className="rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs h-8"
            data-testid={`b2b-card-review-${c.id}`}
          >
            <AlertCircle className="h-3.5 w-3.5 mr-1" /> Review
          </Button>
        )}
        {isAdmin && c.status === STATUS.APPROVED && (
          <Button
            size="sm"
            onClick={onActivate}
            className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
            data-testid={`b2b-card-activate-${c.id}`}
          >
            <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Activate
          </Button>
        )}
      </div>
    </div>
  );
}
